import { CoralogixBrowserSdkConfig, CoralogixRum, CoralogixLogSeverity } from '@coralogix/browser';
import { AppConfig } from '../providers/AppConfigProvider';

const CORALOGIX_RUM_SAMPLING_RATE = 2;

let isRumInitialized = false;

export const initCoralogixRum = (appConfig?: AppConfig): void => {
  const publicKey = process.env.REACT_APP_CORALOGIX_RUM_PUBLIC_KEY;

  if (!publicKey) {
    console.warn('Coralogix RUM public key not found. RUM monitoring will be disabled.');
    return;
  }

  if (isRumInitialized) {
    console.warn('Coralogix RUM is already initialized.');
    return;
  }

  const config: CoralogixBrowserSdkConfig = {
    public_key: publicKey,
    stringifyCustomLogData: true,
    application: 'checkout-v2',
    version: '1.0.0',
    coralogixDomain: (process.env.REACT_APP_CORALOGIX_DOMAIN as 'US2') || 'US2',
    sessionConfig: {
      sessionSampleRate: CORALOGIX_RUM_SAMPLING_RATE,
      alwaysTrackSessionsWithErrors: true
    },
    beforeSend: (event) => {
      if (event.event_context.type === 'log' && event.log_context?.data) {
        const data = event.log_context?.data as Record<string, any> ?? {};
        Object.entries(data).forEach(([key, value]) => {
          (event.log_context as any)[key] = value;
        });
        delete (event.log_context as any).data;
        return event
      }
    },
    user_context: {
      user_id: '',
      user_email: '',
      user_name: '',
    },
    labels: {
      publisherId: appConfig?.publisherId || '',
      publisherName: appConfig?.publisherData?.companyName || '',
      environment: process.env.REACT_APP_ENV || 'production',
      platform: 'web',
    }
  };

  try {
    CoralogixRum.init(config);
    isRumInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Coralogix RUM:', error);
  }
};


export const setRumUserContext = (userContext: {
  user_email: string;
  user_name: string;
  user_id: string;
}): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.setUserContext(userContext);
  } catch (error) {
    console.error('Failed to set RUM user context:', error);
  }
};

export const updateRumCustomerData = (sessionToken: string, customerEmail?: string): void => {
  if (!isRumInitialized) {
    return;
  }

  setRumUserContext({
    user_id: sessionToken,
    user_email: customerEmail || '',
    user_name: '',
  });
};

export const logToCoralogix = (
  level: CoralogixLogSeverity,
  message: string,
  data?: Record<string, any> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.log(level, message, data || undefined, labels);
  } catch (error) {
    console.error('Failed to send log to Coralogix:', error);
  }
};

export const logDebug = (
  message: string,
  data?: Record<string, any> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Debug, message, data, labels);
};


export const logInfo = (
  message: string,
  data?: Record<string, any> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Info, message, data, labels);
};

export const logWarn = (
  message: string,
  data?: Record<string, any> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Warn, message, data, labels);
};

export const logError = (
  message: string,
  errorData?: Error | any,
  additionalData?: Record<string, any>,
  labels?: Record<string, string | number | boolean>
): void => {
  const data = {
    ...additionalData,
    error: errorData ? {
      name: errorData?.name || 'Error',
      message: errorData?.message || String(errorData),
      stack: errorData?.stack,
    } : undefined,
  };

  logToCoralogix(CoralogixLogSeverity.Error, message, data, labels);
};

export const logCritical = (
  message: string,
  data?: Record<string, any> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Critical, message, data, labels);
};

export const reportError = (
  message: string,
  data?: Record<string, any> | null
): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.error(message, data || undefined);
  } catch (err) {
    console.error('Failed to report error to Coralogix:', err);
  }
};

export const setCoralogixLabels = (labels: Record<string, string | number | boolean>): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.setLabels(labels);
  } catch (error) {
    console.error('Failed to set Coralogix labels:', error);
  }
};

export default {
  initCoralogixRum,
  setRumUserContext,
  updateRumCustomerData,
  logToCoralogix,
  logInfo,
  logWarn,
  logError,
  logDebug,
  logCritical,
  reportError,
  setCoralogixLabels,
};
